import { ButtonSharedScreen } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MediaStreamSilentTrack, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toastError } from "../../helpers/toast";
import { useDynamicMedia, useMeetingSelector } from "../../hooks/meeting";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";

export const ScreenMediaName = "screen";

export type ScreenShareStatus = "idle" | "starting" | "ongoing" | "stopping";

const DISPLAY_MEDIA_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {width: {max: 1920}, height: {max: 1080}, frameRate: {ideal: 30}},
  audio:  {echoCancellation: {ideal: false}, autoGainControl: {ideal: false}, noiseSuppression: {ideal: false}},
}

export interface ScreenShareImperativeAPI {
  stop: () => Promise<void>;
}

export interface ScreenShareProps {
  meetingContainerAPI: MeetingContainerAPI | undefined;
  onStatus?: (status: ScreenShareStatus) => void;
  onStream?: (stream: MediaStream) => void;
}

export const ScreenShare = forwardRef(({ meetingContainerAPI, onStatus, onStream }: ScreenShareProps, ref: ForwardedRef<ScreenShareImperativeAPI>) => {
  const [session] = useMeetingSelector(meetingContainerAPI, state => state.session);

  const [status, setStatus] = useState<ScreenShareStatus>("idle");
  const silentAudioTrack = useRef<MediaStreamSilentTrack>();

  const dynamicMediaAPI = useDynamicMedia(meetingContainerAPI, {
    onStreamStart: (_, success) => {
      if (!success && status === "starting") {
        error("could not set media stream");
        cleanup();
        return;
      }
      setStatus("ongoing");
    },
  });
  const dynamicMediaAPIRef = useRefAutoUpdate(dynamicMediaAPI);

  const interactionAvailable = status === "idle" || status === "ongoing";

  const cleanup = async () => {
    const currentStream = dynamicMediaAPIRef.current.get(ScreenMediaName);
    if (currentStream) {
      setStatus("stopping");
      currentStream.getTracks().forEach((track) => track.stop());
    }

    if (silentAudioTrack.current) {
      silentAudioTrack.current.destroy();
      silentAudioTrack.current = undefined;
    }

    setStatus("idle");
  };

  const stop = async () => {
    try {
      await cleanup();
      await dynamicMediaAPIRef.current.remove(ScreenMediaName);
    } catch(err) {
      console.error(err);
    }

  };

  useImperativeHandle(
    ref,
    () => {
      return {
        stop
      };
    },
    []
  );

  useEffect(() => {
    onStatus?.(status);
  }, [status, onStatus]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  const error = (detail: string, error?: any) => {
    toastError("Le partage d'écran a échoué.");
    console.error(`screenshare failed : ${detail}${error ? `\n${error}` : ""}`);
  };
  const onStartShare = async () => {
    if (!interactionAvailable) return;
    if (dynamicMediaAPI.get(ScreenMediaName)) return stop();

    if (!session) return;
    const updater = meetingContainerAPI?.updater;
    if (!updater) return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_CONSTRAINTS);
      if (!stream.getAudioTracks().length) {
        const silentTrack = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
        silentAudioTrack.current = silentTrack;
        stream.addTrack(silentTrack.track);
      }
      const videoTrack = stream.getVideoTracks()[0];
      // warning : 'onStopShare' here will be frozen, important to use refs inside
      if (videoTrack) videoTrack.onended = stop;
    } catch {
      return;
    }

    onStream?.(stream);
    setStatus("starting");

    const success = await dynamicMediaAPI.set(ScreenMediaName, stream, undefined);
    if (!success) {
      cleanup();
    }
  };

  // TODO : needs a disabled props for when it is not rady (!interactionAvailable)
  return <ButtonSharedScreen isEnabled={!!dynamicMediaAPI.get(ScreenMediaName)} onClick={onStartShare} />;
});
